import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getLocalePack } from '../i18n/localeRegistry';
import { setAnalyticsLanguage, trackLanguageChange } from '../utils/analytics';
import { getPathLanguage, localizePath } from '../utils/localizedRoutes';
import { normalizeLanguage } from '../i18n/languages';

const LANGUAGE_STORAGE_KEY = 'site-language';

const LanguageContext = createContext({
  language: 'en',
  setLanguage: () => {},
  localizePath: (path) => path,
  t: (key) => key,
  translateValue: (_, value) => value,
});

function resolvePath(object, path) {
  return path.split('.').reduce((current, segment) => current?.[segment], object);
}

function interpolate(value, replacements = {}) {
  if (typeof value !== 'string') return value;

  return Object.entries(replacements).reduce(
    (current, [key, replacement]) => current.replaceAll(`{${key}}`, String(replacement)),
    value,
  );
}

export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const language = getPathLanguage(location.pathname);
  const previousLanguageRef = useRef(null);

  useEffect(() => {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    document.documentElement.lang = language;
    setAnalyticsLanguage(language);

    if (previousLanguageRef.current && previousLanguageRef.current !== language) {
      trackLanguageChange({
        language,
        previousLanguage: previousLanguageRef.current,
      });
    }

    previousLanguageRef.current = language;
  }, [language]);

  const value = useMemo(() => {
    const setLanguage = (nextLanguage) => {
      const nextPath = localizePath(
        `${location.pathname}${location.search}${location.hash}`,
        nextLanguage,
      );
      const currentPath = `${location.pathname}${location.search}${location.hash}`;

      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, normalizeLanguage(nextLanguage));

      if (nextPath !== currentPath) {
        navigate(nextPath, { state: location.state });
      }
    };

    const t = (key, replacements = {}) => {
      const localePack = getLocalePack(language);
      const activeLanguageValue = resolvePath(localePack.ui, key);
      const fallbackValue = resolvePath(localePack.fallbackUi, key);

      return interpolate(activeLanguageValue ?? fallbackValue ?? key, replacements);
    };

    const translateValue = (group, valueToTranslate) => {
      const localePack = getLocalePack(language);
      const activeLanguageValue = localePack.ui?.[group]?.[valueToTranslate];
      const fallbackValue = localePack.fallbackUi?.[group]?.[valueToTranslate];

      return activeLanguageValue ?? fallbackValue ?? valueToTranslate;
    };

    return {
      language,
      setLanguage,
      localizePath: (path) => localizePath(path, language),
      t,
      translateValue,
    };
  }, [language, location.hash, location.pathname, location.search, location.state, navigate]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}
